import { ModalProps } from 'UIKit/AlchemyModal'
import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import useForceUpdate from 'hooks/useForceUpdate'
import { useImmutableValueReference } from 'hooks/useImmutableValueReference'
import React, { MutableRefObject, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector } from 'state'
import styled, { keyframes } from 'styled-components'

interface ModalsContext {
  onPresent: (id: string, Component: React.FC, data?: any, disableCloseByBackdrop?: boolean) => void
  onDismiss: (id?: string) => void
  closeModal: () => void
  contentsRef: MutableRefObject<{ id: string; content: any }[]>
  contentIds: string[]
}

export interface IModalNavigation {
  onBack?: (param: any) => void
}

export const Context = createContext<ModalsContext>({
  onPresent: () => {},
  onDismiss: () => {},
  closeModal: () => {},
  contentsRef: null,
  contentIds: [],
})

const animationDuration = 250

const OpenModalEffect: React.FC<{
  open: boolean
  onClose: () => void
  display?: string
  zIndex?: string
  children: any
}> = ({ open, onClose, display, children, zIndex }) => {
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        onClose()
      }, animationDuration)
    }
  }, [open])

  return (
    <StyledResponsiveWrapper display={display} zIndex={zIndex} open={open}>
      {children}
    </StyledResponsiveWrapper>
  )
}

const Modals = ({ children }) => {
  const serverTime = useAppSelector((state) => state.app.serverTime)
  const updater = useForceUpdate()

  const [contentId, setContentId] = useState<React.ReactNode>()
  const contentIdRef = useImmutableValueReference(contentId)
  const contentsRef = useRef([])
  const isClosingRef = useRef(false)

  useEffect(() => {
    if (contentId) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
  }, [!!contentId])

  const contentIds = useMemo(() => {
    return contentsRef.current.map((content) => content.id)
  }, [contentId])

  const currentContent = useMemo(() => {
    const ref = contentsRef.current.find((content) => contentId === content.id)
    return { ...ref }
  }, [contentId, updater.dependency])

  const handlePresent = useCallback(
    (id: string, Component: React.FC<ModalProps>, data?: any, disableCloseByBackdrop = false) => {
      const present = () => {
        contentsRef.current.push({
          id,
          disableCloseByBackdrop,
          toggle: true,
          content: (
            <Component
              data={data}
              key={id}
              onDismiss={() => {
                handleDismiss(id)
              }}
            />
          ),
        })
        setContentId(id)
      }

      if (!isClosingRef.current) {
        present()
      } else {
        setTimeout(present, animationDuration + 1000)
      }
    },
    [setContentId],
  )

  const handleDismiss = useCallback((id?: string) => {
    if (id && contentIdRef.current != id) {
      const contents = contentsRef.current.filter((item) => item.id !== id)
      contentsRef.current = contents
    } else {
      const _currentContent = contentsRef.current.find((item) => item.id === contentIdRef.current)
      _currentContent.toggle = false
      isClosingRef.current = true
      updater.update()
    }
  }, [])

  const handleCloseAllModal = useCallback(() => {
    const _currentContent = contentsRef.current.find((item) => item.id === contentIdRef.current)

    if (contentsRef.current.length > 0) {
      contentsRef.current = [_currentContent]
      handleDismiss(_currentContent.id)
    } else {
      setContentId(undefined)
      contentsRef.current = []
    }
  }, [])

  const closeModal = useCallback(() => {
    const contents = contentsRef.current.filter((item) => item.id !== contentId)
    contentsRef.current = contents
    isClosingRef.current = false
    if (contents.length === 0) {
      handleCloseAllModal()
      return
    }
    contentsRef.current = contents
    setContentId(contentsRef.current[contentsRef.current.length - 1].id)
  }, [contentId])

  const value = useMemo(
    () => ({
      onPresent: handlePresent,
      onDismiss: handleDismiss,
      closeModal: handleCloseAllModal,
      contentsRef,
      contentIds,
    }),
    [handlePresent, handleDismiss, handleCloseAllModal],
  )

  return (
    <Context.Provider value={value}>
      {children}
      <StyledModalWrapper
        style={{ pointerEvents: contentId && serverTime ? 'auto' : 'none', zIndex: contentId && serverTime ? 100 : -1 }}
      >
        <Backdrop
          id="modal-backdrop"
          onClick={() => !currentContent.disableCloseByBackdrop && handleDismiss()}
          toggle={contentsRef.current.length === 1 ? currentContent.toggle : !!contentId}
          aria-hidden
        />
        {contentId && serverTime && (
          <>
            {contentsRef.current.map((item) => (
              <StyledModalWrapper
                zIndex={item.id === contentId ? '100' : '98 !important'}
                style={{ pointerEvents: 'none' }}
                key={item.id}
              >
                <OpenModalEffect open={item.id === contentId ? currentContent.toggle : true} onClose={closeModal}>
                  {item.content}
                </OpenModalEffect>
              </StyledModalWrapper>
            ))}
          </>
        )}
      </StyledModalWrapper>
    </Context.Provider>
  )
}

const Backdrop: React.FC<BoxProps & { toggle: boolean }> = ({ onClick, toggle, ...props }) => {
  const isModalOpened = useRef(false)
  useEffect(() => {
    setTimeout(() => {
      isModalOpened.current = true
    }, 300)
  })

  const handleClick = () => {
    if (isModalOpened.current) {
      onClick(null)
    }
  }
  return <StyledModalBackdrop onClick={handleClick} $toggle={toggle} {...props} />
}

const mobileOpen = keyframes`
  0% {
    transform: translate(-100%, 0%);
  }
  100% {
    transform: translate(-100%,-100%);
  }
`

const mobileClose = keyframes`
  0% {
    transform: translate(-100%,-100%);;
  }
  100% {
    transform: translate(-100%,0%);
  }
`

const mobileFullModeOpen = keyframes`
  0% {
    transform: translate(0%,-100%);
  }
  100% {
    transform:translate(-100%,-100%);
  }
`

const mobileFullModeClose = keyframes`
  0% {
    transform: translate(-100%,-100%);
  }
  100% {
    transform: translate(0%,-100%);
  }
`

const desktopOpen = keyframes`
  0% {
    transform: scale(85%);
    opacity: 0.2;
  }
  100% {
    transform: scale(100%);
    opacity: 1;
  }
`

const desktopClose = keyframes`
  0% {
    transform: scale(100%);
    opacity: 1;
  }
  100% {
    transform: scale(85%);
    opacity: 0
  }
`

type StyledResponsiveWrapperProps = {
  display: string
  zIndex?: string
  open: boolean
}

const StyledResponsiveWrapper = styled(Box)<StyledResponsiveWrapperProps>`
  pointer-events: none;
  z-index: ${({ zIndex }) => zIndex};
  align-items: center;
  flex-direction: column;
  justify-content: flex-end;
  position: absolute;
  top: 100%;
  left: 100%;
  width: 100%;
  height: 100%;
  display: flex;
  animation: ${({ open }) => (open ? mobileOpen : mobileClose)} ${animationDuration / 1000}s forwards ease-out;

  &:has(.alchemy-modal[data-size='full']) {
    animation: ${({ open }) => (open ? mobileFullModeOpen : mobileFullModeClose)} ${animationDuration / 1000}s forwards
      ease-out !important;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    position: relative;
    top: unset;
    left: unset;
    width: 100%;
    justify-content: center;

    &:not(div.alchemy-modal[data-size='auto']),
    &:has(.alchemy-modal[data-size='auto']),
    &:has(.alchemy-modal[data-size='full']) {
      animation: ${({ open }) => (open ? desktopOpen : desktopClose)} ${animationDuration / 1000}s forwards ease-out !important;
    }

    animation: ${({ open }) => (open ? desktopOpen : desktopClose)} ${animationDuration / 1000}s forwards ease-out !important;
  }
`

const StyledModalWrapper = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100;
  height: var(--screen-height);
  width: 100vw;
`

const StyledModalBackdrop = styled(Box)<{ $toggle: boolean }>`
  transition: ${animationDuration / 1000}s;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99;
  background: rgba(0, 0, 0, 0);
  backdrop-filter: blur(0px);
  touch-action: none;

  ${({ $toggle }) => $toggle && 'backdrop-filter: blur(1px) !important;'}
  ${({ theme, $toggle }) => $toggle && `background: ${theme.colors.backdrop} !important;`}
`

export default Modals
